import { Link } from 'react-router-dom';
import { ArrowRight, Heart, MessageCircle, Target, Bell, Wallet, Clock, BookOpen, Users, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

export const SectionTitle = ({ eyebrow, title, subtitle, icon: Icon }) => (
  <div className="text-center mb-14">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-kairos-orange/10 border border-kairos-orange/20 mb-5">
        {Icon && <Icon className="w-3.5 h-3.5 text-kairos-orange" />}
        <span className="text-kairos-orange text-xs font-medium tracking-[0.2em]">{eyebrow}</span>
      </div>
    )}
    <h2 className="font-display text-3xl md:text-4xl text-white mb-4">{title}</h2>
    {subtitle && <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">{subtitle}</p>}
  </div>
);

export const PreInscricaoAviso = ({ periodo }) => (
  <section className="relative -mt-10 z-20" data-testid="aviso-pre-inscricao">
    <div className="container mx-auto px-4">
      <div className="max-w-5xl mx-auto rounded-2xl border border-kairos-orange/30 bg-obsidian-100/90 backdrop-blur-md p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
        <div className="w-14 h-14 rounded-2xl bg-kairos-orange/15 flex items-center justify-center flex-shrink-0">
          <Bell className="w-7 h-7 text-kairos-orange" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <p className="text-white font-semibold text-lg">Pré-inscrições abertas para o Kairós {periodo.slice(-4)}</p>
          <p className="text-gray-400 text-sm md:text-base">Deixe os dados do casal e receba em primeira mão a abertura do primeiro lote, valores e condições.</p>
        </div>
        <Link to="/pre-inscricao" data-testid="aviso-pre-inscricao-btn">
          <Button className="rounded-full bg-kairos-orange hover:bg-kairos-orange-dark text-white h-12 px-6">
            Fazer pré-inscrição <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export const SobreKairos = () => (
  <section id="sobre" className="py-24 bg-obsidian scroll-mt-20" data-testid="sobre-section">
    <div className="container mx-auto px-4">
      <SectionTitle eyebrow="O QUE É O KAIRÓS" icon={BookOpen} title={<>Um tempo de Deus para o <span className="text-kairos-orange">casamento</span></>}
        subtitle="O Encontro de Casais Kairós é um seminário psicoteológico imersivo: quatro dias longe da rotina, dedicados a reconstruir a conexão, a comunicação e o propósito do casal, integrando psicologia e espiritualidade." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {[
          { icon: Heart, title: 'Fortalecer a conexão emocional', desc: 'Reconectem-se em um nível profundo, longe das distrações do dia a dia. Redescubram o que os uniu.' },
          { icon: MessageCircle, title: 'Melhorar comunicação e respeito', desc: 'Ferramentas práticas para se comunicar melhor, resolver conflitos e construir respeito mútuo.' },
          { icon: Target, title: 'Reacender propósito e visão conjunta', desc: 'Alinhem sonhos, planos e propósitos. Saiam com uma visão clara do futuro que querem construir juntos.' },
        ].map((item) => (
          <Card key={item.title} className="bg-white/[0.03] border border-white/10 hover:border-kairos-orange/40 hover:-translate-y-1 transition-all duration-300">
            <CardContent className="p-8">
              <div className="w-12 h-12 rounded-xl bg-kairos-orange/10 flex items-center justify-center mb-6"><item.icon className="w-6 h-6 text-kairos-orange" /></div>
              <h3 className="text-white text-lg font-semibold mb-3">{item.title}</h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">{item.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-14">
        {[
          { icon: Clock, label: '4 dias de imersão' },
          { icon: Users, label: 'Exclusivo para casais' },
          { icon: BookOpen, label: 'Conteúdo psicoteológico' },
          { icon: Sparkles, label: 'Momentos a dois' },
        ].map((b) => (
          <div key={b.label} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
            <b.icon className="w-5 h-5 text-kairos-orange flex-shrink-0" />
            <span className="text-gray-300 text-sm">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const PrimeiroLote = () => (
  <section id="primeiro-lote" className="py-24 bg-obsidian-100 relative overflow-hidden scroll-mt-20" data-testid="primeiro-lote-section">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-kairos-orange/8 rounded-full blur-[160px]" />
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-kairos-orange/15 border border-kairos-orange/40 mb-6">
          <Bell className="w-4 h-4 text-kairos-orange" />
          <span className="text-kairos-orange text-xs font-semibold tracking-[0.2em]">EM BREVE</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl text-white mb-6">O PRIMEIRO LOTE ESTÁ <span className="text-kairos-orange">CHEGANDO!</span></h2>
        <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-4">
          Estamos preparando mais uma edição especial do Encontro de Casais Kairós. Em breve, divulgaremos as informações sobre o primeiro lote, os valores de investimento e as condições de inscrição. Enquanto isso, vocês já podem realizar a pré-inscrição e receber as novidades em primeira mão.
        </p>
        <p className="text-white font-medium mb-8">Antecipem seu interesse e façam parte da nossa lista de pré-inscrição.</p>
        <Link to="/pre-inscricao" data-testid="primeiro-lote-btn">
          <Button className="h-14 px-10 rounded-full bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold shadow-[0_0_40px_rgba(255,107,0,0.35)] hover:scale-[1.03] transition-all">
            FAZER PRÉ-INSCRIÇÃO <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export const Investimento2027 = ({ ano }) => (
  <section id="investimento" className="py-24 bg-obsidian scroll-mt-20" data-testid="investimento-section">
    <div className="container mx-auto px-4">
      <SectionTitle eyebrow={`INVESTIMENTO KAIRÓS ${ano}`} icon={Wallet} title={<>VALORES <span className="text-kairos-orange">EM BREVE</span></>} />
      <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10 text-center">
        <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8">
          As informações sobre o investimento, formas de pagamento e condições especiais serão divulgadas juntamente com a abertura do primeiro lote de inscrições.
        </p>
        <Link to="/pre-inscricao" data-testid="investimento-btn">
          <Button variant="outline" className="h-auto min-h-12 py-3 px-6 md:px-8 rounded-full border-kairos-orange/50 text-kairos-orange hover:bg-kairos-orange/10 hover:text-white font-semibold whitespace-normal max-w-full text-sm md:text-base">
            QUERO SER AVISADO SOBRE O PRIMEIRO LOTE
          </Button>
        </Link>
      </div>
    </div>
  </section>
);
